"use client";

import { decodeAudioFile, durationSeconds, frameCount, type PcmAudio } from "mediatools";
import { useTranslations } from "next-intl";
import React, {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type Cue, synthesizeWords } from "subtitletools";
import { cn } from "@/lib/utils";
import type { EditorMediaReference } from "./adapters";
import { isLocalFileMedia } from "./LocalFileSource";
import { isTekstaRunMedia } from "./TekstaRunSource";

const PEAKS_PER_SECOND = 100;
const MIN_HANDLE_MOVE_PX = 2;

type WaveformLabel = "vocals" | "audio";

interface WaveformPeaks {
  peaks: Float32Array;
  binsPerSecond: number;
  duration: number;
  label: WaveformLabel;
}

interface WaveformSource {
  key: string;
  label: WaveformPeaks["label"];
  load: () => Promise<Blob | ArrayBuffer>;
}

const waveformCache = new Map<string, Promise<WaveformPeaks>>();

const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

function waveformSourceForMedia(media: EditorMediaReference): WaveformSource | null {
  if (isLocalFileMedia(media)) {
    return {
      key: `local:${media.name}:${media.size}:${media.lastModified}`,
      label: "audio",
      load: async () => media.file,
    };
  }

  if (!isTekstaRunMedia(media)) return null;
  const url = media.vocalStemUrl ?? media.url;
  return {
    key: `url:${url}`,
    label: media.vocalStemUrl ? "vocals" : "audio",
    load: async () => {
      const response = await fetch(url, { cache: "force-cache", mode: "cors" });
      if (!response.ok) throw new Error("Could not load waveform audio");
      return response.arrayBuffer();
    },
  };
}

function buildPeaks(audio: PcmAudio, label: WaveformPeaks["label"]): WaveformPeaks {
  const duration = durationSeconds(audio);
  const frames = frameCount(audio);
  const binCount = Math.max(1, Math.ceil(duration * PEAKS_PER_SECOND));
  const peaks = new Float32Array(binCount);
  const framesPerBin = Math.max(1, frames / binCount);

  for (let frame = 0; frame < frames; frame += 1) {
    let peak = 0;
    for (const channel of audio.channelData) {
      peak = Math.max(peak, Math.abs(channel[frame] ?? 0));
    }
    const bin = Math.min(binCount - 1, Math.floor(frame / framesPerBin));
    peaks[bin] = Math.max(peaks[bin] ?? 0, peak);
  }

  return { peaks, binsPerSecond: PEAKS_PER_SECOND, duration, label };
}

function loadWaveform(source: WaveformSource): Promise<WaveformPeaks> {
  const cached = waveformCache.get(source.key);
  if (cached) return cached;
  const promise = source
    .load()
    .then((input) => decodeAudioFile(input))
    .then((audio) => buildPeaks(audio, source.label))
    .catch((error) => {
      waveformCache.delete(source.key);
      throw error;
    });
  waveformCache.set(source.key, promise);
  return promise;
}

function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

function drawWaveform(
  canvas: HTMLCanvasElement,
  waveform: WaveformPeaks,
  cueStartSeconds: number,
  cueEndSeconds: number,
  width: number
) {
  const cssHeight = canvas.clientHeight;
  if (width <= 0 || cssHeight <= 0) return;

  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(width * dpr));
  canvas.height = Math.max(1, Math.floor(cssHeight * dpr));

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, cssHeight);
  ctx.strokeStyle = getComputedStyle(canvas).color || "rgb(100 116 139)";
  ctx.lineWidth = 1;

  const cueStart = clamp(cueStartSeconds, 0, waveform.duration);
  const cueEnd = clamp(cueEndSeconds, cueStart, waveform.duration);
  const startBin = Math.floor(cueStart * waveform.binsPerSecond);
  const endBin = Math.max(startBin + 1, Math.ceil(cueEnd * waveform.binsPerSecond));
  const binSpan = Math.max(1, endBin - startBin);
  const mid = cssHeight / 2;
  const ampScale = mid * 0.88;

  ctx.beginPath();
  for (let x = 0; x < width; x += 1) {
    const binA = startBin + Math.floor((x / width) * binSpan);
    const binB = startBin + Math.max(1, Math.floor(((x + 1) / width) * binSpan));
    let peak = 0;
    for (let bin = binA; bin <= binB; bin += 1) {
      peak = Math.max(peak, waveform.peaks[bin] ?? 0);
    }
    const height = Math.max(1, peak * ampScale);
    ctx.moveTo(x + 0.5, mid - height);
    ctx.lineTo(x + 0.5, mid + height);
  }
  ctx.stroke();
}

interface WordTimingStripProps {
  cue: Cue;
  cueIndex: number;
  current: number;
  media: EditorMediaReference;
  onBoundaryChange: (cueIndex: number, boundaryIndex: number, time: number, key: string) => void;
  onCommit: () => void;
}

export function WordTimingStrip({
  cue,
  cueIndex,
  current,
  media,
  onBoundaryChange,
  onCommit,
}: WordTimingStripProps) {
  const t = useTranslations("mediaTools.editor");
  const words = useMemo(() => synthesizeWords(cue), [cue]);
  const source = useMemo(() => waveformSourceForMedia(media), [media]);
  const [waveform, setWaveform] = useState<WaveformPeaks | null>(null);
  const [waveformError, setWaveformError] = useState<string | null>(null);
  const [trackRef, width] = useElementWidth<HTMLDivElement>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{
    boundaryIndex: number;
    key: string;
    pointerId: number;
    startX: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    if (!source) {
      setWaveform(null);
      setWaveformError(t("audio.waveformUnavailable"));
      return;
    }

    let cancelled = false;
    setWaveform(null);
    setWaveformError(null);
    void loadWaveform(source)
      .then((next) => {
        if (!cancelled) setWaveform(next);
      })
      .catch(() => {
        if (!cancelled) setWaveformError(t("audio.waveformUnavailable"));
      });

    return () => {
      cancelled = true;
    };
  }, [source, t]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !waveform) return;
    drawWaveform(canvas, waveform, cue.start, cue.end, width);
  }, [cue.start, cue.end, waveform, width]);

  if (words.length < 2) return null;

  const span = Math.max(0.001, cue.end - cue.start);
  const timeToPct = (time: number) => `${clamp(((time - cue.start) / span) * 100, 0, 100)}%`;
  const clientXToTime = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return cue.start;
    return cue.start + (clamp(clientX - rect.left, 0, rect.width) / Math.max(1, rect.width)) * span;
  };
  const dragKey = (boundaryIndex: number) =>
    `drag-word-boundary:${cue.id ?? cueIndex}:${boundaryIndex}`;

  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, boundaryIndex: number) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      boundaryIndex,
      key: dragKey(boundaryIndex),
      pointerId: event.pointerId,
      startX: event.clientX,
      moved: false,
    };
  };
  const moveDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - drag.startX) >= MIN_HANDLE_MOVE_PX) drag.moved = true;
    if (!drag.moved) return;
    onBoundaryChange(cueIndex, drag.boundaryIndex, clientXToTime(event.clientX), drag.key);
  };
  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (!drag.moved) return;
    onBoundaryChange(cueIndex, drag.boundaryIndex, clientXToTime(event.clientX), drag.key);
    onCommit();
  };

  const playheadPct = current >= cue.start && current <= cue.end ? timeToPct(current) : null;

  return (
    <section
      className="rounded-md bg-background p-2 shadow-sm"
      aria-label={t("audio.wordTimingForCue", { text: cue.text })}
    >
      <div ref={trackRef} className="relative h-24 overflow-hidden rounded bg-foreground/[0.03]">
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute inset-x-0 top-7 bottom-0 h-[calc(100%-1.75rem)] w-full text-foreground/45"
        />
        {waveform === null && (
          <div className="absolute inset-x-2 top-9 text-foreground/45 text-xs">
            {waveformError ?? t("audio.loadingWaveform")}
          </div>
        )}
        <div className="absolute inset-x-0 top-1 h-6">
          {words.map((word, index) => {
            const left = timeToPct(word.start);
            const right = timeToPct(word.end);
            return (
              <div
                key={`${index}:${word.text}`}
                className="absolute top-0 h-5 overflow-hidden rounded-sm bg-primary/10 px-1 text-center text-[11px] text-foreground/75 leading-5 ring-1 ring-primary/15"
                style={{
                  left,
                  width: `calc(${right} - ${left})`,
                }}
                title={word.text}
              >
                <span className="block truncate">{word.text}</span>
              </div>
            );
          })}
        </div>
        {/* Outer edges are boundaries too (index -1 = first word's start,
            last index = last word's end): a word ending short of the cue —
            e.g. after the cue was lengthened — is otherwise unreachable. */}
        {[-1, ...words.keys()].map((index) => {
          const word = words[index];
          const next = words[index + 1];
          const isEdge = word === undefined || next === undefined;
          const left = timeToPct(word?.end ?? next?.start ?? cue.start);
          const label =
            word === undefined
              ? t("audio.setWordStart", { word: next?.text ?? "" })
              : next === undefined
                ? t("audio.setWordEnd", { word: word.text })
                : t("audio.setBoundaryBetween", { word: word.text, nextWord: next.text });
          return (
            <button
              key={`${index}:${word?.text ?? ""}:${next?.text ?? ""}`}
              type="button"
              aria-label={label}
              className={cn(
                "-translate-x-1/2 absolute inset-y-0 z-20 w-5 cursor-ew-resize touch-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
              style={{ left }}
              onPointerDown={(event) => startDrag(event, index)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <span
                className={cn(
                  "-translate-x-1/2 pointer-events-none absolute top-1 bottom-1 left-1/2 w-px",
                  isEdge ? "bg-primary/40" : "bg-primary/70"
                )}
              />
              <span
                className={cn(
                  "-translate-x-1/2 pointer-events-none absolute top-1/2 left-1/2 h-4 w-1.5 -translate-y-1/2 rounded-full",
                  isEdge ? "bg-primary/60" : "bg-primary"
                )}
              />
            </button>
          );
        })}
        {playheadPct && (
          <span
            className="pointer-events-none absolute top-0 bottom-0 z-10 w-px bg-timeline-marker"
            style={{ left: playheadPct }}
          />
        )}
      </div>
    </section>
  );
}
